import { useEffect, useRef, useState } from 'react';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import AjudaTela from '../../components/AjudaTela';
import Layout from '../../components/Layout';
import { api } from '../../services/api';

const LOGO_TAMANHO_MAXIMO_BYTES = 300 * 1024;

const AJUDA = {
  titulo: 'Dados da Empresa',
  descricao:
    'Informações que aparecem no cabeçalho de todos os relatórios gerados em PDF: nome da empresa, telefone e logo. Razão social e CNPJ/CPF são definidos no cadastro do contrato e não podem ser alterados por aqui.',
  secoes: [
    {
      titulo: 'Campos',
      campos: [
        { nome: 'Telefone', obrigatorio: false, explicacao: 'Aparece no cabeçalho de todo relatório exportado em PDF, junto com a cidade/estado da fazenda ativa.' },
        { nome: 'Logo', obrigatorio: false, explicacao: 'Imagem PNG ou JPG, até 300 KB. Aparece no canto esquerdo do cabeçalho dos relatórios. Sem logo cadastrado, o cabeçalho segue sem imagem — nunca com um logo genérico.' },
      ],
    },
  ],
  dicas: [
    'Telefone e logo valem para todas as fazendas deste CNPJ — não é possível ter um logo diferente por fazenda.',
    'Cidade e estado no cabeçalho do relatório vêm da fazenda selecionada no momento da exportação, não desta tela.',
  ],
};

function formatarTelefone(valor) {
  const digitos = (valor || '').replace(/\D/g, '');
  if (digitos.length === 11) return digitos.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (digitos.length === 10) return digitos.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return digitos;
}

export default function DadosEmpresa() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const inputLogoRef = useRef(null);

  useEffect(() => {
    carregar();
  }, []);

  const carregar = async () => {
    setCarregando(true);
    setErro('');
    try {
      const res = await api.get('/configuracoes/dados-empresa/');
      setDados(res.data);
    } catch {
      setErro('Erro ao carregar os dados da empresa.');
    } finally {
      setCarregando(false);
    }
  };

  const salvar = async (campos) => {
    setSalvando(true);
    setErro(''); setSucesso('');
    try {
      const res = await api.patch('/configuracoes/dados-empresa/', campos);
      setDados(res.data);
      setSucesso('Dados salvos com sucesso.');
    } catch (err) {
      const detalhe = err.response?.data?.logo_base64?.[0]
        || err.response?.data?.telefone?.[0]
        || err.response?.data?.detail
        || 'Erro ao salvar os dados da empresa.';
      setErro(detalhe);
    } finally {
      setSalvando(false);
    }
  };

  const escolherLogo = (e) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;
    e.target.value = ''; // permite escolher o mesmo arquivo de novo depois

    if (!['image/png', 'image/jpeg'].includes(arquivo.type)) {
      setErro('Formato inválido — envie uma imagem PNG ou JPG.');
      return;
    }
    if (arquivo.size > LOGO_TAMANHO_MAXIMO_BYTES) {
      setErro('Logo muito grande — o limite é 300 KB. Use uma imagem menor ou mais comprimida.');
      return;
    }

    const leitor = new FileReader();
    leitor.onload = () => salvar({ logo_base64: leitor.result });
    leitor.readAsDataURL(arquivo);
  };

  const removerLogo = () => salvar({ logo_base64: '' });

  return (
    <Layout>
      <div className="p-6 font-medium max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            Dados da Empresa <HiOutlineOfficeBuilding size={30} />
          </h2>
          <AjudaTela {...AJUDA} />
        </div>
        <p className="text-gray-500 text-sm mb-6">
          Usados no cabeçalho de todos os relatórios exportados em PDF.
        </p>

        {erro && <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-700 border border-red-200">{erro}</div>}
        {sucesso && <div className="mb-4 p-3 rounded-lg bg-green-100 text-green-700 border border-green-200">{sucesso}</div>}

        {carregando ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 divide-y divide-gray-200">
            <div className="p-4">
              <p className="font-semibold text-gray-800 mb-1">Razão social</p>
              <p className="text-gray-700">{dados.razao_social}</p>
              <p className="text-sm text-gray-400 mt-1">
                {dados.tipo_pessoa === 'F' ? 'CPF' : 'CNPJ'}: {dados.cnpj} · definido no cadastro do contrato
              </p>
            </div>

            <div className="p-4">
              <p className="font-semibold text-gray-800 mb-1">Telefone</p>
              <p className="text-sm text-gray-500 mb-2">Aparece no cabeçalho dos relatórios, junto da cidade/estado da fazenda.</p>
              <input
                className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                value={formatarTelefone(dados.telefone)}
                placeholder="(00) 00000-0000"
                onChange={(e) => setDados({ ...dados, telefone: e.target.value.replace(/\D/g, '') })}
                onBlur={() => salvar({ telefone: dados.telefone })}
                disabled={salvando}
              />
            </div>

            <div className="p-4">
              <p className="font-semibold text-gray-800 mb-1">Logo</p>
              <p className="text-sm text-gray-500 mb-3">PNG ou JPG, até 300 KB. Aparece no cabeçalho dos relatórios em PDF.</p>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-lg border border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden shrink-0">
                  {dados.logo_base64 ? (
                    <img src={dados.logo_base64} alt="Logo da empresa" className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-xs text-gray-400 text-center px-2">Sem logo</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={inputLogoRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={escolherLogo}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => inputLogoRef.current?.click()}
                    disabled={salvando}
                    className="px-3 py-2 rounded-lg bg-brand-green text-white text-sm font-semibold hover:bg-emerald-900 transition disabled:opacity-60"
                  >
                    {dados.logo_base64 ? 'Trocar logo' : 'Enviar logo'}
                  </button>
                  {dados.logo_base64 && (
                    <button
                      type="button"
                      onClick={removerLogo}
                      disabled={salvando}
                      className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition disabled:opacity-60"
                    >
                      Remover logo
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
